/**
 * Bolt parameter data type resolution.
 *
 * Each bolt configuration field has a predefined (canonical) bolt parameter
 * in the default shared parameter file (PiX_PPG_BoltSet group) with a fixed
 * Revit data type. When the user types a parameter name that is missing from
 * the shared parameter file, the host would otherwise create it as plain
 * Text — breaking downstream consumers that expect a Length diameter, an
 * Integer quantity, or a Yes/No toggle. These hints carry the intended type
 * alongside the parameter names so ad-hoc creation uses the correct type.
 */

import type { SharedParameterDataType } from '../../types/sharedParameter.types';
import type { BoltConfigurationInput } from './planner';

export interface BoltParameterTypeHint {
  name: string;
  dataType: SharedParameterDataType;
}

type BoltParameterField = keyof Pick<
  BoltConfigurationInput,
  | 'boltSetActiveValue'
  | 'boltItemDescriptionValue'
  | 'boltDiaValue'
  | 'boltQuantityValue'
  | 'boltLengthValue'
  | 'boltMaterialValue'
  | 'boltStandardValue'
>;

/**
 * Data type of the predefined bolt parameter behind each configuration field
 * (see shared/constants/defaultSharedParameters.ts, PiX_PPG_BoltSet group).
 */
export const BOLT_FIELD_DATA_TYPES: Record<BoltParameterField, SharedParameterDataType> = {
  boltSetActiveValue: 'YESNO',
  boltItemDescriptionValue: 'TEXT',
  boltDiaValue: 'LENGTH',
  boltQuantityValue: 'INTEGER',
  boltLengthValue: 'LENGTH',
  boltMaterialValue: 'TEXT',
  boltStandardValue: 'TEXT',
};

/**
 * Resolve per-parameter data type hints for the bolt parameters selected in
 * the configuration. Field order matches extractBoltParameterNames so that
 * when the same name is assigned to multiple fields, the earliest field wins.
 */
export function resolveBoltParameterTypeHints(
  config: BoltConfigurationInput
): BoltParameterTypeHint[] {
  const hints: BoltParameterTypeHint[] = [];
  const seenNames = new Set<string>();

  (
    Object.entries(BOLT_FIELD_DATA_TYPES) as Array<[BoltParameterField, SharedParameterDataType]>
  ).forEach(([field, dataType]) => {
    const name = config[field]?.trim();
    if (!name) {
      return;
    }
    const key = name.toLowerCase();
    if (seenNames.has(key)) {
      return;
    }
    seenNames.add(key);
    hints.push({ name, dataType });
  });

  return hints;
}
